import { supabase } from './supabase';
import { renderEmailTemplateToHtml } from './emailTemplateRenderer';
import { sendSMS } from './messaging';
import { withSender, resolveCurrentSender } from './messagesCore';
import { findExistingCustomer, updateCustomerFromBooking } from './customers';
import { 
  AutomationV2, 
  AutomationNode,
  AutomationConnection,
  AutomationExecution,
  AutomationExecutionStep,
  TriggerNode,
  ActionNode,
  WireConnection,
  CreateAutomationRequest,
  CreateNodeRequest,
  CreateConnectionRequest,
  BookingTriggerConfig,
  FeedbackTriggerConfig,
  SMSActionConfig
} from '../types/automationsV2';
import { getBooking } from './bookings';
import { BookingFeedback } from './feedback';
import {
  buildOpportunityAutomationData,
  resolveStageName,
  type OpportunityAutomationData,
} from './crm/opportunityAutomationContext';

// ==================== HELPER FUNCTIONS ====================

// Convert database AutomationNode to frontend TriggerNode
function convertNodeToTrigger(node: AutomationNode): TriggerNode {
  const isZapierWebhook =
    node.node_type === 'trigger' && node.node_subtype === 'zapier';

  // Zapier-style webhooks are stored as subtype `zapier` for `zapier-automation-webhook`,
  // but the canvas and all UI logic use trigger type `webhook`.
  const frontendType = isZapierWebhook ? 'webhook' : node.node_subtype;
  const configuration = { ...(node.configuration || {}) };
  if (isZapierWebhook) {
    configuration.webhook_delivery = 'zapier';
  }

  return {
    id: node.id,
    type: frontendType,
    name: node.name,
    position: { x: node.position_x, y: node.position_y },
    config: configuration,
  };
}

// Convert database AutomationNode to frontend ActionNode
function convertNodeToAction(node: AutomationNode): ActionNode {
  return {
    id: node.id,
    type: node.node_subtype, // 'sms', 'email', etc.
    name: node.name,
    position: { x: node.position_x, y: node.position_y },
    config: node.configuration
  };
}

// Convert database AutomationConnection to frontend WireConnection
function convertConnectionToWire(connection: AutomationConnection): WireConnection {
  return {
    id: connection.id,
    fromNodeId: connection.source_node_id,
    toNodeId: connection.target_node_id,
    fromPort: 'outflow',
    toPort: 'inflow',
    crm_branch: connection.crm_branch ?? undefined
  };
}

// Convert frontend TriggerNode to database format
function convertTriggerToNode(trigger: TriggerNode, automationId: string): Partial<AutomationNode> {
  const configuration = { ...(trigger.config || {}) };

  let nodeSubtype = trigger.type;
  if (trigger.type === 'webhook') {
    const useZapier = configuration.webhook_delivery === 'zapier';
    nodeSubtype = useZapier ? 'zapier' : 'webhook';
    if (useZapier) {
      configuration.webhook_delivery = 'zapier';
    } else {
      delete configuration.webhook_delivery;
    }
  }

  return {
    automation_id: automationId,
    node_type: 'trigger',
    node_subtype: nodeSubtype,
    name: trigger.name,
    position_x: trigger.position.x,
    position_y: trigger.position.y,
    configuration,
  };
}

// Convert frontend ActionNode to database format
function convertActionToNode(action: ActionNode, automationId: string): Partial<AutomationNode> {
  return {
    automation_id: automationId,
    node_type: 'action',
    node_subtype: action.type, // 'sms', 'email', etc.
    name: action.name,
    position_x: action.position.x,
    position_y: action.position.y,
    configuration: action.config
  };
}

// ==================== AUTOMATION CRUD ====================

// Get current business ID (you may need to adapt this to your auth system)
export async function getCurrentBusinessId(): Promise<string | null> {
  if (!supabase) return null;
  
  try {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return null;

    const { data, error } = await supabase
      .from('business_users')
      .select('business_id')
      .eq('user_id', user.id)
      .maybeSingle();

    if (data?.business_id) return data.business_id;

    // Fallback: check resources table for resource accounts
    const { data: resource } = await supabase
      .from('resources')
      .select('business_id')
      .eq('auth_user_id', user.id)
      .eq('status', 'active')
      .maybeSingle();

    return resource?.business_id || null;
  } catch (error) {
    console.error('Error getting current business ID:', error);
    return null;
  }
}

// Get all automations for a business
export async function getAutomations(businessId: string): Promise<AutomationV2[]> {
  if (!supabase) throw new Error('Supabase client not initialized');
  
  try {
    const { data, error } = await supabase
      .from('automations_v2')
      .select('*')
      .eq('business_id', businessId)
      .order('created_at', { ascending: false });

    if (error) throw error;
    return data || [];
  } catch (error) {
    console.error('Error fetching automations:', error);
    throw error;
  }
}

/** Automations that have an "Enter Automation" receiver trigger (for Add to Automation action). */
export async function getReceiverAutomationOptions(
  businessId: string,
  opts?: { excludeAutomationId?: string }
): Promise<Array<{ id: string; name: string }>> {
  if (!supabase) throw new Error('Supabase client not initialized');

  const { data: nodes, error } = await supabase
    .from('automation_nodes')
    .select('automation_id')
    .eq('node_type', 'trigger')
    .eq('node_subtype', 'enter_automation');

  if (error) throw error;

  const ids = [
    ...new Set(
      (nodes || [])
        .map((n: { automation_id?: string }) => n.automation_id)
        .filter((id): id is string => !!id)
    ),
  ];
  if (ids.length === 0) return [];

  const { data: autos, error: aerr } = await supabase
    .from('automations_v2')
    .select('id, name')
    .eq('business_id', businessId)
    .in('id', ids)
    .order('name', { ascending: true });

  if (aerr) throw aerr;

  let list = (autos || []).map((a: { id: string; name: string }) => ({ id: a.id, name: a.name }));
  if (opts?.excludeAutomationId) {
    list = list.filter((a) => a.id !== opts.excludeAutomationId);
  }
  return list;
}

// Create a new automation
export async function createAutomation(request: CreateAutomationRequest): Promise<AutomationV2> {
  if (!supabase) throw new Error('Supabase client not initialized');
  
  try {
    const { data, error } = await supabase
      .from('automations_v2')
      .insert({
        business_id: request.business_id,
        name: request.name,
        description: request.description,
        is_live: false
      })
      .select()
      .single();

    if (error) throw error;
    return data;
  } catch (error) {
    console.error('Error creating automation:', error);
    throw error;
  }
}

// Toggle automation live status
export async function toggleAutomationLive(automationId: string, isLive: boolean): Promise<AutomationV2> {
  if (!supabase) throw new Error('Supabase client not initialized');
  
  try {
    const { data, error } = await supabase
      .from('automations_v2')
      .update({ is_live: isLive })
      .eq('id', automationId)
      .select()
      .single();

    if (error) throw error;
    return data;
  } catch (error) {
    console.error('Error toggling automation live status:', error);
    throw error;
  }
}

// Update automation details
export async function updateAutomation(automationId: string, updates: { name?: string }): Promise<AutomationV2> {
  if (!supabase) throw new Error('Supabase client not initialized');
  
  try {
    const { data, error } = await supabase
      .from('automations_v2')
      .update(updates)
      .eq('id', automationId)
      .select()
      .single();

    if (error) throw error;
    return data;
  } catch (error) {
    console.error('Error updating automation:', error);
    throw error;
  }
}

// Delete automation and all related data
export async function deleteAutomation(automationId: string): Promise<void> {
  if (!supabase) throw new Error('Supabase client not initialized');
  
  try {
    // Delete all related data in the correct order
    
    // Delete automation execution steps first (get execution IDs first)
    const { data: executions } = await supabase
      .from('automation_executions')
      .select('id')
      .eq('automation_id', automationId);

    if (executions && executions.length > 0) {
      const executionIds = executions.map(e => e.id);
      const { error: stepsError } = await supabase
        .from('automation_execution_steps')
        .delete()
        .in('execution_id', executionIds);

      if (stepsError && stepsError.code !== 'PGRST116') {
        throw new Error(`Failed to delete automation execution steps: ${stepsError.message}`);
      }
    }

    // Delete automation executions
    const { error: executionsError } = await supabase
      .from('automation_executions')
      .delete()
      .eq('automation_id', automationId);

    if (executionsError && executionsError.code !== 'PGRST116') {
      throw new Error(`Failed to delete automation executions: ${executionsError.message}`);
    }

    // Delete automation jobs
    const { error: jobsError } = await supabase
      .from('automation_jobs')
      .delete()
      .eq('automation_id', automationId);

    if (jobsError && jobsError.code !== 'PGRST116') {
      throw new Error(`Failed to delete automation jobs: ${jobsError.message}`);
    }

    // Delete automation connections
    const { error: connectionsError } = await supabase
      .from('automation_connections')
      .delete()
      .eq('automation_id', automationId);

    if (connectionsError && connectionsError.code !== 'PGRST116') {
      throw new Error(`Failed to delete automation connections: ${connectionsError.message}`);
    }

    // Delete automation nodes
    const { error: nodesError } = await supabase
      .from('automation_nodes')
      .delete()
      .eq('automation_id', automationId);

    if (nodesError && nodesError.code !== 'PGRST116') {
      throw new Error(`Failed to delete automation nodes: ${nodesError.message}`);
    }

    // Finally delete the automation itself
    const { error } = await supabase
      .from('automations_v2')
      .delete()
      .eq('id', automationId);

    if (error) {
      throw new Error(`Failed to delete automation: ${error.message}`);
    }
  } catch (error) {
    console.error('Error deleting automation:', error);
    throw error;
  }
}

// ==================== NODE MANAGEMENT ====================

// Get all nodes for an automation
export async function getAutomationNodes(automationId: string): Promise<AutomationNode[]> {
  if (!supabase) throw new Error('Supabase client not initialized');
  
  try {
    const { data, error } = await supabase
      .from('automation_nodes')
      .select('*')
      .eq('automation_id', automationId)
      .order('created_at', { ascending: true });

    if (error) throw error;
    return data || [];
  } catch (error) {
    console.error('Error fetching automation nodes:', error);
    throw error;
  }
}

// Get trigger nodes for an automation (converted to TriggerNode format for frontend)
export async function getAutomationTriggers(automationId: string): Promise<TriggerNode[]> {
  if (!supabase) throw new Error('Supabase client not initialized');
  
  try {
    const { data, error } = await supabase
      .from('automation_nodes')
      .select('*')
      .eq('automation_id', automationId)
      .eq('node_type', 'trigger')
      .order('created_at', { ascending: true });

    if (error) throw error;
    return (data || []).map(convertNodeToTrigger);
  } catch (error) {
    console.error('Error fetching automation triggers:', error);
    throw error;
  }
}

// Get action nodes for an automation (converted to ActionNode format for frontend)
export async function getAutomationActions(automationId: string): Promise<ActionNode[]> {
  if (!supabase) throw new Error('Supabase client not initialized');
  
  try {
    const { data, error } = await supabase
      .from('automation_nodes')
      .select('*')
      .eq('automation_id', automationId)
      .eq('node_type', 'action')
      .order('created_at', { ascending: true });

    if (error) throw error;
    return (data || []).map(convertNodeToAction);
  } catch (error) {
    console.error('Error fetching automation actions:', error);
    throw error;
  }
}

// Create a new node
export async function createAutomationNode(request: CreateNodeRequest): Promise<AutomationNode> {
  if (!supabase) throw new Error('Supabase client not initialized');
  
  try {
    const { data, error } = await supabase
      .from('automation_nodes')
      .insert({
        automation_id: request.automation_id,
        node_type: request.node_type,
        node_subtype: request.node_subtype,
        name: request.name,
        position_x: request.position_x || 100,
        position_y: request.position_y || 100,
        configuration: request.configuration || {}
      })
      .select()
      .single();

    if (error) throw error;
    return data;
  } catch (error) {
    console.error('Error creating automation node:', error);
    throw error;
  }
}

// Update an existing node
export async function updateAutomationNode(nodeId: string, updates: Partial<AutomationNode>): Promise<AutomationNode | null> {
  if (!supabase) throw new Error('Supabase client not initialized');
  
  try {
    // First check if the node exists
    const { data: existingNode, error: checkError } = await supabase
      .from('automation_nodes')
      .select('id')
      .eq('id', nodeId)
      .single();
    
    // If node doesn't exist, return null instead of throwing
    if (checkError || !existingNode) {
      console.warn(`⚠️ Node ${nodeId} does not exist in database, cannot update`);
      return null;
    }
    
    const { data, error } = await supabase
      .from('automation_nodes')
      .update(updates)
      .eq('id', nodeId)
      .select()
      .single();

    if (error) throw error;
    return data;
  } catch (error: any) {
    // If it's a "no rows" error, return null instead of throwing
    if (error?.code === 'PGRST116') {
      console.warn(`⚠️ Node ${nodeId} does not exist in database, cannot update`);
      return null;
    }
    console.error('Error updating automation node:', error);
    throw error;
  }
}

// Delete a node
export async function deleteAutomationNode(nodeId: string): Promise<void> {
  if (!supabase) throw new Error('Supabase client not initialized');
  
  try {
    const { error } = await supabase
      .from('automation_nodes')
      .delete()
      .eq('id', nodeId);

    if (error) throw error;
  } catch (error) {
    console.error('Error deleting automation node:', error);
    throw error;
  }
}

// Save triggers (for frontend compatibility)
// Returns a map of temporary IDs to database IDs for newly created nodes
export async function saveAutomationTriggers(automationId: string, triggers: TriggerNode[]): Promise<Map<string, string>> {
  if (!supabase) throw new Error('Supabase client not initialized');
  
  const idMap = new Map<string, string>();
  
  try {
    // Get existing trigger nodes
    const { data: existingNodes } = await supabase
      .from('automation_nodes')
      .select('*')
      .eq('automation_id', automationId)
      .eq('node_type', 'trigger');

    const existingIds = new Set((existingNodes || []).map(n => n.id));
    const currentIds = new Set(triggers.map(t => t.id));

    // Delete nodes that are no longer present
    for (const node of existingNodes || []) {
      if (!currentIds.has(node.id)) {
        await deleteAutomationNode(node.id);
      }
    }

    // Update or create nodes
    for (const trigger of triggers) {
      const nodeData = convertTriggerToNode(trigger, automationId);
      
      // Check if ID is a valid UUID (36 chars with dashes) and exists in database
      const isExistingUuid = trigger.id.length === 36 && trigger.id.includes('-') && existingIds.has(trigger.id);
      
      if (isExistingUuid) {
        // Update existing node - ID stays the same
        const updated = await updateAutomationNode(trigger.id, nodeData);
        if (updated) {
          idMap.set(trigger.id, trigger.id);
        } else {
          // Node doesn't exist, create it instead
          console.log(`⚠️ Node ${trigger.id} not found, creating new node instead`);
          const createdNode = await createAutomationNode({
            automation_id: automationId,
            node_type: 'trigger',
            node_subtype: trigger.type,
            name: trigger.name,
            position_x: trigger.position.x,
            position_y: trigger.position.y,
            configuration: trigger.config
          });
          idMap.set(trigger.id, createdNode.id);
        }
      } else {
        // Create new node - get the database UUID
        const createdNode = await createAutomationNode({
          automation_id: automationId,
          node_type: 'trigger',
          node_subtype: trigger.type,
          name: trigger.name,
          position_x: trigger.position.x,
          position_y: trigger.position.y,
          configuration: trigger.config
        });
        // Map temporary ID to database UUID
        idMap.set(trigger.id, createdNode.id);
      }
    }
    
    return idMap;
  } catch (error) {
    console.error('Error saving automation triggers:', error);
    throw error;
  }
}

// Save actions (for frontend compatibility)
// Returns a map of temporary IDs to database IDs for newly created nodes
export async function saveAutomationActions(automationId: string, actions: ActionNode[]): Promise<Map<string, string>> {
  if (!supabase) throw new Error('Supabase client not initialized');
  
  const idMap = new Map<string, string>();
  
  try {
    // Get existing action nodes
    const { data: existingNodes } = await supabase
      .from('automation_nodes')
      .select('*')
      .eq('automation_id', automationId)
      .eq('node_type', 'action');

    const existingIds = new Set((existingNodes || []).map(n => n.id));
    const currentIds = new Set(actions.map(a => a.id));

    // Delete nodes that are no longer present
    for (const node of existingNodes || []) {
      if (!currentIds.has(node.id)) {
        await deleteAutomationNode(node.id);
      }
    }

    // Update or create nodes
    for (const action of actions) {
      const nodeData = convertActionToNode(action, automationId);
      
      // Check if ID is a valid UUID (36 chars with dashes) and exists in database
      const isExistingUuid = action.id.length === 36 && action.id.includes('-') && existingIds.has(action.id);
      
      if (isExistingUuid) {
        // Update existing node - ID stays the same
        const updated = await updateAutomationNode(action.id, nodeData);
        if (updated) {
          idMap.set(action.id, action.id);
        } else {
          // Node doesn't exist, create it instead
          console.log(`⚠️ Node ${action.id} not found, creating new node instead`);
          const createdNode = await createAutomationNode({
            automation_id: automationId,
            node_type: 'action',
            node_subtype: action.type,
            name: action.name,
            position_x: action.position.x,
            position_y: action.position.y,
            configuration: action.config
          });
          idMap.set(action.id, createdNode.id);
        }
      } else {
        // Create new node - get the database UUID
        const createdNode = await createAutomationNode({
          automation_id: automationId,
          node_type: 'action',
          node_subtype: action.type,
          name: action.name,
          position_x: action.position.x,
          position_y: action.position.y,
          configuration: action.config
        });
        // Map temporary ID to database UUID
        idMap.set(action.id, createdNode.id);
      }
    }
    
    return idMap;
  } catch (error) {
    console.error('Error saving automation actions:', error);
    throw error;
  }
}

// ==================== CONNECTION MANAGEMENT ====================

// Get all connections for an automation
export async function getAutomationConnections(automationId: string): Promise<WireConnection[]> {
  if (!supabase) throw new Error('Supabase client not initialized');
  
  try {
    const { data, error } = await supabase
      .from('automation_connections')
      .select('*')
      .eq('automation_id', automationId)
      .order('created_at', { ascending: true });

    if (error) throw error;
    return (data || []).map(convertConnectionToWire);
  } catch (error) {
    console.error('Error fetching automation connections:', error);
    throw error;
  }
}

// Create a new connection
export async function createAutomationConnection(request: CreateConnectionRequest): Promise<AutomationConnection> {
  if (!supabase) throw new Error('Supabase client not initialized');
  
  try {
    // Validate that both nodes exist before creating connection
    const { data: sourceNode, error: sourceError } = await supabase
      .from('automation_nodes')
      .select('id')
      .eq('id', request.source_node_id)
      .single();
    
    if (sourceError || !sourceNode) {
      throw new Error(`Source node ${request.source_node_id} does not exist in database`);
    }
    
    const { data: targetNode, error: targetError } = await supabase
      .from('automation_nodes')
      .select('id')
      .eq('id', request.target_node_id)
      .single();
    
    if (targetError || !targetNode) {
      throw new Error(`Target node ${request.target_node_id} does not exist in database`);
    }
    
    const { data, error } = await supabase
      .from('automation_connections')
      .insert({
        automation_id: request.automation_id,
        source_node_id: request.source_node_id,
        target_node_id: request.target_node_id,
        ...(request.crm_branch === 'left' || request.crm_branch === 'right'
          ? { crm_branch: request.crm_branch }
          : {})
      })
      .select()
      .single();

    if (error) throw error;
    return data;
  } catch (error) {
    console.error('Error creating automation connection:', error);
    throw error;
  }
}

// Delete a connection
export async function deleteAutomationConnection(connectionId: string): Promise<void> {
  if (!supabase) throw new Error('Supabase client not initialized');
  
  try {
    const { error } = await supabase
      .from('automation_connections')
      .delete()
      .eq('id', connectionId);

    if (error) throw error;
  } catch (error) {
    console.error('Error deleting automation connection:', error);
    throw error;
  }
}

// Save wire connections (for frontend compatibility)
export async function saveWireConnections(automationId: string, connections: WireConnection[]): Promise<void> {
  if (!supabase) throw new Error('Supabase client not initialized');
  
  try {
    // First, verify all node IDs in connections exist in the database
    const allNodeIds = new Set<string>();
    connections.forEach(conn => {
      allNodeIds.add(conn.fromNodeId);
      allNodeIds.add(conn.toNodeId);
    });
    
    if (allNodeIds.size > 0) {
      const { data: existingNodes, error: nodesError } = await supabase
        .from('automation_nodes')
        .select('id')
        .eq('automation_id', automationId)
        .in('id', Array.from(allNodeIds));
      
      if (nodesError) {
        console.error('Error verifying nodes exist:', nodesError);
        throw nodesError;
      }
      
      const existingNodeIds = new Set((existingNodes || []).map(n => n.id));
      const missingNodeIds = Array.from(allNodeIds).filter(id => !existingNodeIds.has(id));
      
      if (missingNodeIds.length > 0) {
        console.warn('⚠️ Some node IDs in connections do not exist in database:', missingNodeIds);
        // Filter out connections with missing nodes
        const validConnections = connections.filter(conn => 
          existingNodeIds.has(conn.fromNodeId) && existingNodeIds.has(conn.toNodeId)
        );
        
        if (validConnections.length !== connections.length) {
          console.warn(`⚠️ Filtered out ${connections.length - validConnections.length} invalid connections`);
          // Continue with only valid connections
          connections = validConnections;
        }
      }
    }
    
    // Get existing connections
    const { data: existingConnections } = await supabase
      .from('automation_connections')
      .select('*')
      .eq('automation_id', automationId);

    const existingIds = new Set((existingConnections || []).map(c => c.id));
    const currentIds = new Set(connections.map(c => c.id));

    // Create a map of existing connections by source/target to prevent duplicates
    const existingByNodes = new Map<string, string>();
    (existingConnections || []).forEach(conn => {
      const key = `${conn.source_node_id}-${conn.target_node_id}`;
      existingByNodes.set(key, conn.id);
    });

    // Delete connections that are no longer present (by ID)
    for (const connection of existingConnections || []) {
      if (!currentIds.has(connection.id)) {
        await deleteAutomationConnection(connection.id);
      }
    }

    // Create new connections (check both by ID and by source/target to prevent duplicates)
    for (const wireConnection of connections) {
      const nodeKey = `${wireConnection.fromNodeId}-${wireConnection.toNodeId}`;
      const existsByNodes = existingByNodes.has(nodeKey);
      const existsById = existingIds.has(wireConnection.id);
      
      // Only create if it doesn't exist by ID AND doesn't exist by source/target
      if (!existsById && !existsByNodes) {
        try {
          // Validate nodes exist before creating connection (createAutomationConnection does this, but we can provide better error)
          await createAutomationConnection({
            automation_id: automationId,
            source_node_id: wireConnection.fromNodeId,
            target_node_id: wireConnection.toNodeId
          });
        } catch (createError: any) {
          // If it's a unique constraint violation, the connection already exists - that's okay
          if (createError?.code === '23505' || createError?.message?.includes('unique')) {
            console.log('⚠️ Connection already exists (unique constraint), skipping:', nodeKey);
          } else if (createError?.code === '23503') {
            // Foreign key violation - node doesn't exist
            console.error(`❌ Cannot create connection: node does not exist in database. Connection: ${nodeKey}`, createError);
            // Don't throw - just log and skip this connection
            // This can happen if nodes were deleted or not yet saved
            console.warn('⚠️ Skipping connection due to missing node:', nodeKey);
          } else {
            throw createError; // Re-throw if it's a different error
          }
        }
      }
    }
  } catch (error) {
    console.error('Error saving wire connections:', error);
    throw error;
  }
}

// ==================== EXECUTION TRACKING ====================

// Create a new automation execution record
export async function createAutomationExecution(
  automationId: string, 
  triggerData: Record<string, any>
): Promise<AutomationExecution> {
  if (!supabase) throw new Error('Supabase client not initialized');
  
  try {
    const { data, error } = await supabase
      .from('automation_executions')
      .insert({
        automation_id: automationId,
        trigger_data: triggerData,
        status: 'running'
      })
      .select()
      .single();

    if (error) throw error;
    return data;
  } catch (error) {
    console.error('Error creating automation execution:', error);
    throw error;
  }
}

// Update execution status and completion
export async function updateAutomationExecution(
  executionId: string,
  updates: {
    status?: 'running' | 'completed' | 'failed' | 'cancelled';
    completed_at?: string;
    error_message?: string;
  }
): Promise<AutomationExecution> {
  if (!supabase) throw new Error('Supabase client not initialized');
  
  try {
    const updateData: any = { ...updates };
    
    // Auto-set completed_at if status is final
    if (updates.status && ['completed', 'failed', 'cancelled'].includes(updates.status)) {
      updateData.completed_at = new Date().toISOString();
    }

    const { data, error } = await supabase
      .from('automation_executions')
      .update(updateData)
      .eq('id', executionId)
      .select()
      .single();

    if (error) throw error;
    return data;
  } catch (error) {
    console.error('Error updating automation execution:', error);
    throw error;
  }
}

// Get executions for an automation
export async function getAutomationExecutions(automationId: string): Promise<AutomationExecution[]> {
  if (!supabase) throw new Error('Supabase client not initialized');
  
  try {
    const { data, error } = await supabase
      .from('automation_executions')
      .select('*')
      .eq('automation_id', automationId)
      .order('started_at', { ascending: false });

    if (error) throw error;
    return data || [];
  } catch (error) {
    console.error('Error fetching automation executions:', error);
    throw error;
  }
}

// Get execution summary/statistics
export async function getExecutionSummary(automationId: string): Promise<{
  total_executions: number;
  successful_executions: number;
  failed_executions: number;
  average_duration_ms: number;
  last_execution?: AutomationExecution;
}> {
  if (!supabase) throw new Error('Supabase client not initialized');
  
  try {
    const { data: executions, error } = await supabase
      .from('automation_executions')
      .select('*')
      .eq('automation_id', automationId)
      .order('started_at', { ascending: false });

    if (error) throw error;

    const total = executions?.length || 0;
    const successful = executions?.filter(e => e.status === 'completed').length || 0;
    const failed = executions?.filter(e => e.status === 'failed').length || 0;
    
    // Calculate average duration for completed executions
    const completedExecutions = executions?.filter(e => e.status === 'completed' && e.completed_at) || [];
    const totalDuration = completedExecutions.reduce((sum, exec) => {
      const start = new Date(exec.started_at).getTime();
      const end = new Date(exec.completed_at!).getTime();
      return sum + (end - start);
    }, 0);
    
    const averageDuration = completedExecutions.length > 0 ? totalDuration / completedExecutions.length : 0;

    return {
      total_executions: total,
      successful_executions: successful,
      failed_executions: failed,
      average_duration_ms: averageDuration,
      last_execution: executions?.[0]
    };
  } catch (error) {
    console.error('Error fetching execution summary:', error);
    throw error;
  }
}

// Get execution steps
export async function getExecutionSteps(executionId: string): Promise<AutomationExecutionStep[]> {
  if (!supabase) throw new Error('Supabase client not initialized');
  
  try {
    // First try with a more explicit join
    const { data, error } = await supabase
      .from('automation_execution_steps')
      .select(`
        *,
        automation_nodes!inner(
          node_type,
          node_subtype,
          name,
          configuration
        )
      `)
      .eq('execution_id', executionId)
      .order('step_order', { ascending: true });

    if (error) {
      console.error('Error fetching execution steps with join:', error);
      
      // Fallback: Get steps and nodes separately
      const [stepsResult, nodesResult] = await Promise.all([
        supabase
          .from('automation_execution_steps')
          .select('*')
          .eq('execution_id', executionId)
          .order('step_order', { ascending: true }),
        supabase
          .from('automation_nodes')
          .select('*')
      ]);

      if (stepsResult.error) throw stepsResult.error;
      if (nodesResult.error) throw nodesResult.error;

      // Manually join the data
      const stepsWithNodes = (stepsResult.data || []).map(step => {
        const node = nodesResult.data?.find(n => n.id === step.node_id);
        return {
          ...step,
          automation_nodes: node ? {
            node_type: node.node_type,
            node_subtype: node.node_subtype,
            name: node.name,
            configuration: node.configuration
          } : null
        };
      });

      console.log('📋 Fallback execution steps with manual join:', stepsWithNodes);
      return stepsWithNodes.map(step => ({
        ...step,
        automation_node: step.automation_nodes
      }));
    }

    console.log('📋 Execution steps with join:', data);
    return (data || []).map(step => ({
      ...step,
      automation_node: step.automation_nodes
    }));
  } catch (error) {
    console.error('Error fetching execution steps:', error);
    throw error;
  }
}

// ==================== AUTOMATION EXECUTION ENGINE ====================

// Execute individual automation actions with result return
async function executeAutomationActionWithResult(
  executionId: string,
  node: AutomationNode,
  stepOrder: number,
  contextData: Record<string, any>
): Promise<Record<string, any> | null> {
  if (!supabase) throw new Error('Supabase client not initialized');

  // Create execution step record
  const { data: step, error: stepError } = await supabase
    .from('automation_execution_steps')
    .insert({
      execution_id: executionId,
      node_id: node.id,
      step_order: stepOrder,
      input_data: contextData,
      status: 'running',
      started_at: new Date().toISOString()
    })
    .select()
    .single();

  if (stepError) throw stepError;

  try {
    let result: Record<string, any> = {};

    // Execute action based on node type
    switch (node.node_subtype) {
      case 'sms':
        result = await executeSMSAction(node, contextData);
        break;
      
      case 'email':
        result = await executeEmailAction(node, contextData);
        break;
      
      case 'wait':
        result = await executeWaitAction(node, contextData);
        break;
      
      case 'create_crm_lead':
        result = await executeCreateCRMLeadAction(node, contextData);
        break;

      case 'add_to_automation':
        result = {
          simulated: true,
          message:
            'Add to Automation runs only in live execution (starts the target automation with the current context).'
        };
        break;

      case 'send_live_intake_form':
        result = {
          simulated: true,
          message:
            'Send Live Form runs in live automation execution (after Create CRM Lead) and is not simulated here.'
        };
        break;

      case 'feedback_agent':
        result = {
          simulated: true,
          message:
            'Feedback Agent runs only in live execution (places one AI call to the customer for a star rating).'
        };
        break;
      
      default:
        throw new Error(`Unknown action type: ${node.node_subtype}`);
    }

    // Update step as completed
    await supabase
      .from('automation_execution_steps')
      .update({
        status: 'completed',
        completed_at: new Date().toISOString(),
        output_data: result
      })
      .eq('id', step.id);

    console.log(`✅ Executed action: ${node.name} (${node.node_subtype})`);
    return result; // Return the result for context enrichment
  } catch (error) {
    // Update step as failed
    await supabase
      .from('automation_execution_steps')
      .update({
        status: 'failed',
        completed_at: new Date().toISOString(),
        error_message: error instanceof Error ? error.message : 'Unknown error'
      })
      .eq('id', step.id);

    console.error(`❌ Failed to execute action: ${node.name}`, error);
    throw error;
  }
}

// Legacy wrapper for backward compatibility
async function executeAutomationAction(
  executionId: string,
  node: AutomationNode,
  stepOrder: number,
  contextData: Record<string, any>
): Promise<void> {
  if (!supabase) throw new Error('Supabase client not initialized');

  // Create execution step record
  const { data: step, error: stepError } = await supabase
    .from('automation_execution_steps')
    .insert({
      execution_id: executionId,
      node_id: node.id,
      step_order: stepOrder,
      input_data: contextData,
      status: 'running',
      started_at: new Date().toISOString()
    })
    .select()
    .single();

  if (stepError) throw stepError;

  try {
    let result: Record<string, any> = {};

    // Execute action based on node type
    switch (node.node_subtype) {
      case 'sms':
        result = await executeSMSAction(node, contextData);
        break;
      
      case 'email':
        result = await executeEmailAction(node, contextData);
        break;
      
      case 'wait':
        result = await executeWaitAction(node, contextData);
        break;
      
      case 'create_crm_lead':
        result = await executeCreateCRMLeadAction(node, contextData);
        break;

      case 'add_to_automation':
        result = {
          simulated: true,
          message:
            'Add to Automation runs only in live execution (starts the target automation with the current context).'
        };
        break;

      case 'send_live_intake_form':
        result = {
          simulated: true,
          message:
            'Send Live Form runs in live automation execution (after Create CRM Lead) and is not simulated here.'
        };
        break;

      case 'feedback_agent':
        result = {
          simulated: true,
          message:
            'Feedback Agent runs only in live execution (places one AI call to the customer for a star rating).'
        };
        break;
      
      default:
        throw new Error(`Unknown action type: ${node.node_subtype}`);
    }

    // Update step as completed
    await supabase
      .from('automation_execution_steps')
      .update({
        status: 'completed',
        completed_at: new Date().toISOString(),
        output_data: result
      })
      .eq('id', step.id);

    console.log(`✅ Executed action: ${node.name} (${node.node_subtype})`);
  } catch (error) {
    // Update step as failed
    await supabase
      .from('automation_execution_steps')
      .update({
        status: 'failed',
        completed_at: new Date().toISOString(),
        error_message: error instanceof Error ? error.message : 'Unknown error'
      })
      .eq('id', step.id);

    console.error(`❌ Failed to execute action: ${node.name}`, error);
    throw error;
  }
}

// Helper function to format dates for SMS messages
function formatDateForSMS(dateString: string): string {
  try {
    // Handle various date formats
    const date = new Date(dateString);
    
    // Check if date is valid
    if (isNaN(date.getTime())) {
      console.warn('Invalid date format for SMS:', dateString);
      return dateString; // Return original if parsing fails
    }
    
    // Format as "April 10, 2025"
    const options: Intl.DateTimeFormatOptions = {
      year: 'numeric',
      month: 'long', 
      day: 'numeric'
    };
    
    return date.toLocaleDateString('en-US', options);
  } catch (error) {
    console.warn('Error formatting date for SMS:', dateString, error);
    return dateString; // Return original if any error occurs
  }
}

// Helper function to format times for SMS messages
function formatTimeForSMS(timeString: string): string {
  try {
    // Handle various time formats (HH:MM, H:MM, etc.)
    if (!timeString) return timeString;
    
    // If it's already in AM/PM format, return as is
    if (timeString.toLowerCase().includes('am') || timeString.toLowerCase().includes('pm')) {
      return timeString;
    }
    
    // Parse time string (assume HH:MM format)
    const timeMatch = timeString.match(/^(\d{1,2}):(\d{2})(?::(\d{2}))?/);
    if (!timeMatch) {
      console.warn('Invalid time format for SMS:', timeString);
      return timeString;
    }
    
    let hours = parseInt(timeMatch[1], 10);
    const minutes = timeMatch[2];
    
    // Convert to 12-hour format with AM/PM
    const ampm = hours >= 12 ? 'PM' : 'AM';
    hours = hours % 12;
    hours = hours ? hours : 12; // Handle midnight (0 hours = 12 AM)
    
    // Format as "9:00 AM" or "1:30 PM"
    return `${hours}:${minutes} ${ampm}`;
  } catch (error) {
    console.warn('Error formatting time for SMS:', timeString, error);
    return timeString; // Return original if any error occurs
  }
}

// Execute SMS action
async function executeSMSAction(
  node: AutomationNode,
  contextData: Record<string, any>
): Promise<Record<string, any>> {
  const config = node.configuration as SMSActionConfig;
  const bookingData = contextData.booking_data;
  
  console.log('📱 SMS Action Debug Info:');
  console.log('- Node config:', config);
  console.log('- Context data keys:', Object.keys(contextData));
  console.log('- Booking data structure:', JSON.stringify(bookingData, null, 2));
  console.log('- Customer data:', bookingData?.customer);
  console.log('- Schedule data:', bookingData?.schedule);
  console.log('- Business data:', bookingData?.business);
  console.log('- Business ID from context:', contextData.business_id);
  
  if (!config.message || !config.recipient) {
    throw new Error('SMS action missing required configuration');
  }

  // Process message with dynamic data
  let processedMessage = config.message;
  
  // Customer variables
  if (bookingData?.customer?.full_name || bookingData?.customer?.name || contextData.customer?.name) {
    const customerName = bookingData?.customer?.full_name || bookingData?.customer?.name || contextData.customer?.name;
    processedMessage = processedMessage.replace(/\{customer_name\}/g, customerName);
  }
  if (bookingData?.customer?.phone || contextData.customer?.phone) {
    processedMessage = processedMessage.replace(/\{customer_phone\}/g, bookingData?.customer?.phone || contextData.customer?.phone);
  }
  if (bookingData?.customer?.email || contextData.customer?.email) {
    processedMessage = processedMessage.replace(/\{customer_email\}/g, bookingData?.customer?.email || contextData.customer?.email);
  }
  
  // Missed call variables
  if (contextData.call_data?.caller_name || contextData.customer?.name) {
    processedMessage = processedMessage.replace(/\{caller_name\}/g, contextData.call_data?.caller_name || contextData.customer?.name || 'Unknown Caller');
  }
  if (contextData.call_data?.caller_phone || contextData.call_data?.from_number) {
    processedMessage = processedMessage.replace(/\{caller_phone\}/g, contextData.call_data?.caller_phone || contextData.call_data?.from_number);
  }
  if (contextData.call_data?.call_time) {
    const callTime = new Date(contextData.call_data.call_time);
    const formattedCallTime = callTime.toLocaleString('en-US', { 
      month: 'short', 
      day: 'numeric', 
      hour: 'numeric', 
      minute: '2-digit',
      hour12: true 
    });
    processedMessage = processedMessage.replace(/\{call_time\}/g, formattedCallTime);
  }
  
  // Booking variables
  if (bookingData?.booking_id || contextData.booking_id) {
    processedMessage = processedMessage.replace(/\{booking_number\}/g, bookingData?.booking_id || contextData.booking_id);
  }
  if (bookingData?.title || contextData.title) {
    processedMessage = processedMessage.replace(/\{booking_title\}/g, bookingData?.title || contextData.title);
  }
  if (bookingData?.grand_total || contextData.grand_total) {
    processedMessage = processedMessage.replace(/\{grand_total\}/g, bookingData?.grand_total || contextData.grand_total);
  }
  
  // Schedule variables with date formatting
  if (bookingData?.schedule?.start_date || contextData.schedule?.start_date) {
    const startDate = bookingData?.schedule?.start_date || contextData.schedule?.start_date;
    const formattedStartDate = formatDateForSMS(startDate);
    processedMessage = processedMessage.replace(/\{start_date\}/g, formattedStartDate);
  }
  if (bookingData?.schedule?.start_time || contextData.schedule?.start_time) {
    const startTime = bookingData?.schedule?.start_time || contextData.schedule?.start_time;
    const formattedStartTime = formatTimeForSMS(startTime);
    processedMessage = processedMessage.replace(/\{start_time\}/g, formattedStartTime);
  }
  if (bookingData?.schedule?.drop_off_date || contextData.schedule?.drop_off_date) {
    const dropOffDate = bookingData?.schedule?.drop_off_date || contextData.schedule?.drop_off_date;
    const formattedDropOffDate = formatDateForSMS(dropOffDate);
    processedMessage = processedMessage.replace(/\{drop_off_date\}/g, formattedDropOffDate);
  }
  if (bookingData?.schedule?.drop_off_time || contextData.schedule?.drop_off_time) {
    const dropOffTime = bookingData?.schedule?.drop_off_time || contextData.schedule?.drop_off_time;
    const formattedDropOffTime = formatTimeForSMS(dropOffTime);
    processedMessage = processedMessage.replace(/\{drop_off_time\}/g, formattedDropOffTime);
  }
  


  // Handle different recipient types
  if (config.recipient === 'resource_phone') {
    // Special handling for resource phone - send to all assigned resources
    const resources = bookingData?.resources || bookingData?.booking_resources || [];
    console.log('📱 SMS Action Debug - Resource Phone Processing:');
    console.log('📱 Raw booking data keys:', Object.keys(bookingData || {}));
    console.log('📱 Found resources in booking data:', resources);
    console.log('📱 Number of resources found:', resources ? resources.length : 0);
    
    if (!resources || resources.length === 0) {
      throw new Error('No resources assigned to this booking');
    }
    
    // Extract phone numbers from resources
    console.log('📱 Individual resource details:');
    resources.forEach((resource: any, index: number) => {
      console.log(`📱   Resource ${index + 1}:`, {
        id: resource?.id,
        name: resource?.name,
        phone: resource?.phone,
        role: resource?.role,
        nested_phone: resource?.resource?.phone
      });
    });
    
    const resourcePhones = resources
      .map((resource: any) => resource?.phone || resource?.resource?.phone)
      .filter((phone: string) => phone && phone.trim() !== '');
    
    console.log('📱 Extracted phone numbers:', resourcePhones);
    
    if (resourcePhones.length === 0) {
      throw new Error('No phone numbers found for assigned resources');
    }
    
    console.log('📱 SMS Details (Multiple Resources):');
    console.log('- To:', resourcePhones);
    console.log('- Message:', processedMessage);
    console.log('- Business ID:', contextData.business_id);
    
    // Send SMS to all resource phone numbers
    const smsResults = [];
    const failedSends = [];
    
    for (const phone of resourcePhones) {
      try {
        const result = await sendSMS({
          businessId: contextData.business_id,
          to: phone,
          message: processedMessage
        });
        
        if (result.success) {
          smsResults.push({
            recipient: phone,
            success: true,
            result: result
          });
          console.log(`📱 SMS sent successfully to resource: ${phone}`);
        } else {
          failedSends.push({
            recipient: phone,
            error: result.error || 'SMS sending failed'
          });
          console.error(`📱 SMS failed to resource: ${phone}`, result.error);
        }
      } catch (error) {
        failedSends.push({
          recipient: phone,
          error: error instanceof Error ? error.message : 'Unknown error'
        });
        console.error(`📱 SMS sending failed to resource: ${phone}`, error);
      }
    }
    
    // Return comprehensive result
    const allSuccessful = failedSends.length === 0;
    
    return {
      success: allSuccessful,
      message: processedMessage,
      sent_at: new Date().toISOString(),
      total_recipients: resourcePhones.length,
      successful_sends: smsResults.length,
      failed_sends: failedSends.length,
      results: smsResults,
      failures: failedSends.length > 0 ? failedSends : undefined
    };
  } else {
    // Handle single recipient cases
    let recipientPhone: string | undefined;
    
    switch (config.recipient) {
      case 'customer_phone':
        recipientPhone = bookingData?.customer?.phone || contextData.customer?.phone;
        if (!recipientPhone) throw new Error('Customer phone not available');
        break;
      case 'caller_phone':
        // For missed call triggers - get the caller's phone number
        recipientPhone = contextData.call_data?.caller_phone || contextData.call_data?.from_number || contextData.customer?.phone;
        if (!recipientPhone) throw new Error('Caller phone not available');
        break;
      case 'business_phone':
        recipientPhone = bookingData?.business?.phone || contextData.business?.phone;
        if (!recipientPhone) throw new Error('Business phone not available');
        break;
      case 'custom':
        recipientPhone = config.customPhone;
        if (!recipientPhone) throw new Error('Custom phone number not provided');
        break;
      default:
        throw new Error(`Unknown recipient type: ${config.recipient}`);
    }

    console.log('📱 SMS Details:');
    console.log('- To:', recipientPhone);
    console.log('- Message:', processedMessage);
    console.log('- Business ID:', contextData.business_id);

    // Send SMS using the existing messaging function
    try {
      const result = await sendSMS({
        businessId: contextData.business_id,
        to: recipientPhone,
        message: processedMessage
      });

      if (!result.success) {
        throw new Error(result.error || 'SMS sending failed');
      }

      console.log('📱 SMS sent successfully via messaging.ts');

      return {
        success: true,
        recipient: recipientPhone,
        message: processedMessage,
        sent_at: new Date().toISOString(),
        sms_result: result
      };
    } catch (error) {
      console.error('📱 SMS sending failed:', error);
      throw new Error(`Failed to send SMS: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }
}

// Execute email action using Gmail integration
async function executeEmailAction(
  node: AutomationNode,
  contextData: Record<string, any>
): Promise<Record<string, any>> {
  const config = node.configuration as any;
  const bookingData = contextData.booking_data;
  const formData = contextData.form_data;
  
  console.log('📧 Email Action Debug Info:');
  console.log('- Node config:', config);
  console.log('- Context data keys:', Object.keys(contextData));
  console.log('- Booking data structure:', JSON.stringify(bookingData, null, 2));
  console.log('- Form data:', formData);
  console.log('- Business ID from context:', contextData.business_id);
  
  if (!config.recipient) {
    throw new Error('Email action missing recipient configuration');
  }

  // If using template, fetch and process template content
  if (config.useTemplate && config.selectedTemplateId) {
    console.log('📧 Fetching email template:', config.selectedTemplateId);
    
    try {
      const { data: template, error: templateError } = await supabase
        .from('email_templates')
        .select('*')
        .eq('id', config.selectedTemplateId)
        .single();

      if (templateError || !template) {
        console.error('❌ Failed to fetch email template:', templateError);
        throw new Error('Selected email template not found');
      }

      // Override subject and message with template content
      config.subject = template.subject || template.content?.subject || '';
      
      // Fetch business data for template processing
      let businessData = null;
      try {
        const { data: business, error: businessError } = await supabase
          .from('businesses')
          .select('name, booking_settings')
          .eq('id', contextData.business_id)
          .single();

        if (!businessError && business) {
          businessData = business;
          console.log('📧 Business data fetched for template:', business.name);
        }
      } catch (error) {
        console.error('❌ Error fetching business data for template:', error);
      }

      // Convert template content to HTML
      if (template.content?.sections) {
        config.message = renderEmailTemplateToHtml({
          sections: template.content.sections,
          businessName: businessData?.name || 'Your Business',
          logoUrl: businessData?.booking_settings?.logo_url,
        });
      } else if (template.content) {
        config.message = typeof template.content === 'string' ? template.content : '';
      }
      
      console.log('📧 Template processed successfully');
    } catch (error) {
      console.error('❌ Error processing email template:', error);
      throw new Error('Failed to process email template');
    }
  }

  // Validate we have subject and message
  if (!config.subject || !config.message) {
    throw new Error('Email action missing subject or message configuration');
  }

  // Process subject and message with dynamic data
  let processedSubject = config.subject;
  let processedMessage = config.message;
  
  // Helper function to replace variables in text
  const replaceVariables = (text: string): string => {
    let result = text;
    
    // Customer variables
    if (bookingData?.customer?.full_name || bookingData?.customer?.name || contextData.customer?.name) {
      const customerName = bookingData?.customer?.full_name || bookingData?.customer?.name || contextData.customer?.name;
      result = result.replace(/\{customer_name\}/g, customerName);
    }
    if (bookingData?.customer?.first_name || contextData.customer?.first_name) {
      result = result.replace(/\{first_name\}/g, bookingData?.customer?.first_name || contextData.customer?.first_name);
    }
    if (bookingData?.customer?.last_name || contextData.customer?.last_name) {
      result = result.replace(/\{last_name\}/g, bookingData?.customer?.last_name || contextData.customer?.last_name);
    }
    if (bookingData?.customer?.phone || contextData.customer?.phone) {
      result = result.replace(/\{customer_phone\}/g, bookingData?.customer?.phone || contextData.customer?.phone);
    }
    if (bookingData?.customer?.email || contextData.customer?.email) {
      result = result.replace(/\{customer_email\}/g, bookingData?.customer?.email || contextData.customer?.email);
    }
    
    // Booking variables
    if (bookingData?.booking_id || contextData.booking_id) {
      result = result.replace(/\{booking_number\}/g, bookingData?.booking_id || contextData.booking_id);
    }
    if (bookingData?.title || contextData.title) {
      result = result.replace(/\{booking_title\}/g, bookingData?.title || contextData.title);
    }
    if (bookingData?.grand_total || contextData.grand_total) {
      result = result.replace(/\{grand_total\}/g, bookingData?.grand_total || contextData.grand_total);
    }
    
    // Schedule variables with date formatting
    if (bookingData?.schedule?.start_date || contextData.schedule?.start_date) {
      const startDate = bookingData?.schedule?.start_date || contextData.schedule?.start_date;
      const formattedStartDate = formatDateForSMS(startDate);
      result = result.replace(/\{start_date\}/g, formattedStartDate);
    }
    if (bookingData?.schedule?.start_time || contextData.schedule?.start_time) {
      const startTime = bookingData?.schedule?.start_time || contextData.schedule?.start_time;
      const formattedStartTime = formatTimeForSMS(startTime);
      result = result.replace(/\{start_time\}/g, formattedStartTime);
    }
    if (bookingData?.schedule?.drop_off_date || contextData.schedule?.drop_off_date) {
      const dropOffDate = bookingData?.schedule?.drop_off_date || contextData.schedule?.drop_off_date;
      const formattedDropOffDate = formatDateForSMS(dropOffDate);
      result = result.replace(/\{drop_off_date\}/g, formattedDropOffDate);
    }
    if (bookingData?.schedule?.drop_off_time || contextData.schedule?.drop_off_time) {
      const dropOffTime = bookingData?.schedule?.drop_off_time || contextData.schedule?.drop_off_time;
      const formattedDropOffTime = formatTimeForSMS(dropOffTime);
      result = result.replace(/\{drop_off_time\}/g, formattedDropOffTime);
    }
    
    return result;
  };

  processedSubject = replaceVariables(processedSubject);
  processedMessage = replaceVariables(processedMessage);

  // Determine recipient email addresses
  let recipientEmails: string[] = [];

  switch (config.recipient) {
    case 'customer_email':
      // Handle both booking and form data contexts
      let customerEmail;
      if (formData) {
        // Extract from form data (similar to SMS logic)
        const customerInfoFields = Object.keys(formData).filter(key => key.includes('_email'));
        for (const key of customerInfoFields) {
          if (formData[key] && formData[key].trim()) {
            customerEmail = formData[key];
            break;
          }
        }
        if (!customerEmail && formData.email) {
          customerEmail = formData.email;
        }
      } else if (bookingData) {
        customerEmail = bookingData.customer?.email;
      }
      
      if (customerEmail && customerEmail.trim()) {
        recipientEmails.push(customerEmail);
      } else {
        throw new Error('Customer email not available');
      }
      break;

    case 'business_email':
      const businessEmail = bookingData?.business?.email || contextData.business?.email;
      if (businessEmail && businessEmail.trim()) {
        recipientEmails.push(businessEmail);
      } else {
        throw new Error('Business email not available');
      }
      break;

    case 'custom':
      if (config.customEmail && config.customEmail.trim()) {
        recipientEmails.push(config.customEmail);
      } else {
        throw new Error('Custom email address not provided');
      }
      break;

    default:
      throw new Error(`Unknown recipient type: ${config.recipient}`);
  }

  console.log('📧 Email Details:');
  console.log('- To:', recipientEmails);
  console.log('- Subject:', processedSubject);
  console.log('- Message length:', processedMessage.length);
  console.log('- Business ID:', contextData.business_id);

  // Build attachment storage paths if PDF attachment configured
  const attachmentStoragePaths = config.pdfAttachment
    ? [{ path: config.pdfAttachment.storagePath, filename: config.pdfAttachment.filename, type: 'application/pdf' }]
    : undefined;

  // Send emails using the Gmail send email function
  try {
    const results = [];
    for (const email of recipientEmails) {
      try {
        const emailResult = await supabase.functions.invoke('gmail-send-email', {
          body: {
            to: email,
            subject: processedSubject,
            body: processedMessage,
            businessId: contextData.business_id,
            sender: await resolveCurrentSender(contextData.business_id),
            ...(attachmentStoragePaths && attachmentStoragePaths.length > 0 && { attachmentStoragePaths })
          }
        });

        if (emailResult.error) {
          throw new Error(emailResult.error.message || 'Email sending failed');
        }

        if (!emailResult.data?.success) {
          throw new Error(emailResult.data?.error || 'Email sending failed');
        }

        results.push({
          recipient: email,
          success: true,
          result: emailResult.data
        });

        console.log(`📧 Email sent successfully to: ${email}`);
      } catch (error) {
        results.push({
          recipient: email,
          success: false,
          error: error instanceof Error ? error.message : 'Unknown error'
        });
        console.error(`📧 Email failed to: ${email}`, error);
      }
    }

    // Return comprehensive result
    const allSuccessful = results.every(r => r.success);
    
    return {
      success: allSuccessful,
      subject: processedSubject,
      message: processedMessage,
      sent_at: new Date().toISOString(),
      total_recipients: recipientEmails.length,
      successful_sends: results.filter(r => r.success).length,
      failed_sends: results.filter(r => !r.success).length,
      results: results.filter(r => r.success),
      failures: results.filter(r => !r.success).length > 0 ? results.filter(r => !r.success) : undefined
    };
  } catch (error) {
    console.error('📧 Email sending failed:', error);
    throw new Error(`Failed to send email: ${error instanceof Error ? error.message : 'Unknown error'}`);
  }
}

// Execute create CRM lead action
async function executeCreateCRMLeadAction(
  node: AutomationNode,
  contextData: Record<string, any>
): Promise<Record<string, any>> {
  if (!supabase) throw new Error('Supabase client not initialized');
  
  const config = node.configuration as any;
  const formData = contextData.form_data || {};
  
  console.log('🎯 CRM Lead Action Debug Info:');
  console.log('- Node config:', config);
  console.log('- Context data keys:', Object.keys(contextData));
  console.log('- Form data:', formData);
  
  const useSplit = !!(config as any).useCrmAdvancedSplit;
  const left = (config as any).crmSplitLeft || {};
  const right = (config as any).crmSplitRight || {};
  let effectivePipeline = config.selectedPipeline;
  let effectiveStage = config.selectedStage;

  if (useSplit) {
    if (!left.selectedPipeline || !left.selectedStage || !right.selectedPipeline || !right.selectedStage) {
      throw new Error('CRM Lead advanced split: configure left and right pipeline and stage');
    }
    if ((config as any).useCrmAiRouting) {
      console.warn(
        '[CRM Lead] AI routing is configured; this browser preview uses the left path only. Run the live automation to use OpenAI routing.'
      );
    }
    effectivePipeline = left.selectedPipeline;
    effectiveStage = left.selectedStage;
  }

  if (!effectivePipeline || !effectiveStage) {
    throw new Error('CRM Lead action missing pipeline or stage configuration');
  }

  try {
    // Extract customer information from form data
    const customerData = {
      first_name: formData['first_name'] || formData['firstName'] || '',
      last_name: formData['last_name'] || formData['lastName'] || '',
      email: formData['email'] || '',
      phone: formData['phone'] || formData['phone_number'] || ''
    };

    console.log('📝 Customer data extracted:', customerData);

    // Create or find customer using the same logic as online booking
    let customerId: string;
    
    console.log('🔍 Looking for existing customer with:', {
      business_id: contextData.business_id,
      email: customerData.email,
      phone: customerData.phone
    });

    const existingCustomer = await findExistingCustomer(
      contextData.business_id,
      customerData.email,
      customerData.phone
    );

    if (existingCustomer) {
      console.log('👤 Found existing customer:', {
        id: existingCustomer.id,
        first_name: existingCustomer.first_name,
        last_name: existingCustomer.last_name,
        email: existingCustomer.email,
        phone: existingCustomer.phone
      });
      
      // Update the existing customer with any new information (same as online booking)
      const updatedCustomer = await updateCustomerFromBooking(
        existingCustomer.id,
        {
          first_name: customerData.first_name,
          last_name: customerData.last_name,
          email: customerData.email,
          phone: customerData.phone
        }
      );
      
      customerId = existingCustomer.id;
      console.log('✅ Updated existing customer:', updatedCustomer);
    } else {
      // Create a new customer (same structure as online booking)
      console.log('🆕 No matching customer found. Creating new customer');
      const { data: newCustomer, error: customerError } = await supabase
        .from('customers')
        .insert({
          business_id: contextData.business_id,
          first_name: customerData.first_name,
          last_name: customerData.last_name,
          email: customerData.email,
          phone: customerData.phone,
          source: 'Lead Form',
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString()
        })
        .select('id')
        .single();

      if (customerError) throw customerError;
      if (!newCustomer?.id) throw new Error('Failed to create customer record');
      
      customerId = newCustomer.id;
      console.log('✨ Created new customer:', customerId);
    }

    // Map field values from form data or custom values
    const mapFieldValue = (fieldMapping: { type: 'form_field' | 'custom', value: string } | string): string => {
      // Handle new string format with embedded variables
      if (typeof fieldMapping === 'string') {
        let result = fieldMapping;
        
        // Replace simple customer variables with actual form field values
        const cleanVariableMap: Record<string, string> = {
          '{{first_name}}': findCustomerInfoFieldValue('first_name'),
          '{{last_name}}': findCustomerInfoFieldValue('last_name'),
          '{{email}}': findCustomerInfoFieldValue('email'),
          '{{phone}}': findCustomerInfoFieldValue('phone')
        };
        
        // Replace clean variables
        for (const [cleanVar, actualValue] of Object.entries(cleanVariableMap)) {
          result = result.replace(new RegExp(cleanVar.replace(/[{}]/g, '\\$&'), 'g'), actualValue);
        }
        
        // Replace any remaining form field variables {{field_id}}
        result = result.replace(/\{\{([^}]+)\}\}/g, (match, fieldId) => {
          return formData[fieldId] || match; // Keep original if field not found
        });
        
        return result;
      }
      
      // Handle old format for backwards compatibility
      if (fieldMapping.type === 'form_field') {
        return formData[fieldMapping.value] || '';
      }
      return fieldMapping.value || '';
    };

    // Helper function to find customer info field values
    const findCustomerInfoFieldValue = (fieldType: string): string => {
      // Look for customer info component fields in form data
      for (const [key, value] of Object.entries(formData)) {
        if (key.includes(`_${fieldType}`) && typeof value === 'string') {
          return value;
        }
      }
      return '';
    };

    // Prepare opportunity data
    const opportunityData = {
      business_id: contextData.business_id,
      customer_id: customerId,
      title: mapFieldValue(config.title),
      pipeline_id: effectivePipeline,
      stage_id: effectiveStage,
      value: parseFloat(mapFieldValue(config.value)) || 0,
      business_name: mapFieldValue(config.business_name) || null,
      source: mapFieldValue(config.source) || 'Lead Form',
      tags: mapFieldValue(config.tags) ? JSON.stringify([mapFieldValue(config.tags)]) : null,
      status: config.status || 'Open'
    };

    console.log('💼 Opportunity data prepared:', opportunityData);

    // Create the opportunity
    const { data: opportunity, error: opportunityError } = await supabase
      .from('pipeline_opportunities')
      .insert(opportunityData)
      .select()
      .single();

    if (opportunityError) throw opportunityError;

    console.log('🎉 CRM Lead created successfully:', opportunity.id);

    void triggerEnterStageAutomations(String(contextData.business_id), opportunity.id, {
      previousStageId: null,
    });

    return {
      success: true,
      customer_id: customerId,
      opportunity_id: opportunity.id,
      opportunity_title: opportunity.title,
      pipeline_id: opportunity.pipeline_id,
      stage_id: opportunity.stage_id,
      created_at: new Date().toISOString()
    };

  } catch (error) {
    console.error('❌ Failed to create CRM lead:', error);
    throw new Error(`Failed to create CRM lead: ${error instanceof Error ? error.message : 'Unknown error'}`);
  }
}

// Convert a wall-clock (date,time) in an IANA timezone to the correct UTC instant.
// Mirrors the edge-function logic so reminder math is timezone-correct everywhere.
function _tzOffsetMs(date: Date, timeZone: string): number {
  const dtf = new Intl.DateTimeFormat('en-US', {
    timeZone,
    hour12: false,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  });
  const parts: Record<string, string> = {};
  for (const p of dtf.formatToParts(date)) parts[p.type] = p.value;
  const hour = parts.hour === '24' ? 0 : Number(parts.hour);
  const asUTC = Date.UTC(
    Number(parts.year),
    Number(parts.month) - 1,
    Number(parts.day),
    hour,
    Number(parts.minute),
    Number(parts.second)
  );
  return asUTC - date.getTime();
}

function _zonedWallClockToUtc(dateStr: string, timeStr: string, timeZone: string): Date {
  const [y, mo, d] = String(dateStr).split('-').map(Number);
  const timeParts = String(timeStr || '00:00').split(':').map(Number);
  const asUTC = Date.UTC(y, (mo || 1) - 1, d || 1, timeParts[0] || 0, timeParts[1] || 0, timeParts[2] || 0);
  const offset1 = _tzOffsetMs(new Date(asUTC), timeZone);
  const offset2 = _tzOffsetMs(new Date(asUTC - offset1), timeZone);
  return new Date(asUTC - offset2);
}

// Execute wait action with support for both duration and reminder modes
async function executeWaitAction(
  node: AutomationNode,
  contextData: Record<string, any>
): Promise<Record<string, any>> {
  console.log('🕒 Executing wait action:', node.id);
  
  try {
    const config = node.configuration || {};
    const waitType = config.wait_type || 'duration';
    
    if (waitType === 'reminder') {
      // Reminder mode - calculate wait time based on booking schedule
      const reminderHoursBefore = config.reminder_hours_before || 24;
      const reminderReference = config.reminder_reference || 'drop_off_datetime';
      
      console.log(`🔔 Reminder mode: ${reminderHoursBefore} hours before ${reminderReference}`);
      
      // Extract booking data from context
      const bookingData = contextData.booking_data || contextData;
      const schedule = bookingData?.schedule;
      
      if (!schedule) {
        console.error('❌ No schedule data found for reminder calculation');
        return {
          success: false,
          message: 'No schedule data available for reminder calculation'
        };
      }
      
      // Get reference date and time
      let referenceDate: string;
      let referenceTime: string;
      
      if (reminderReference === 'drop_off_datetime') {
        referenceDate = schedule.drop_off_date || schedule.start_date;
        referenceTime = schedule.drop_off_time || schedule.start_time || '12:00';
      } else {
        referenceDate = schedule.start_date || schedule.drop_off_date;
        referenceTime = schedule.start_time || schedule.drop_off_time || '12:00';
      }
      
      if (!referenceDate) {
        console.error('❌ No reference date found for reminder calculation');
        return {
          success: false,
          message: 'No reference date available for reminder calculation'
        };
      }
      
      // Calculate target reminder time
      try {
        // Interpret the booking's wall-clock date/time in the business timezone so
        // the reminder fires at the correct local time (not the runtime's UTC).
        const businessTimezone =
          contextData.business_timezone ||
          contextData.booking_data?.business_timezone ||
          'America/Vancouver';
        const appointmentDateTime = _zonedWallClockToUtc(referenceDate, referenceTime, businessTimezone);
        const reminderDateTime = new Date(appointmentDateTime.getTime() - (reminderHoursBefore * 60 * 60 * 1000));
        const currentTime = new Date();
        
        console.log(`🌐 Business timezone: ${businessTimezone}`);
        console.log(`📅 Appointment time: ${appointmentDateTime.toISOString()}`);
        console.log(`⏰ Reminder time: ${reminderDateTime.toISOString()}`);
        console.log(`🕐 Current time: ${currentTime.toISOString()}`);
        
        // Calculate wait time in milliseconds
        const waitTimeMs = reminderDateTime.getTime() - currentTime.getTime();
        
        if (waitTimeMs <= 0) {
          console.log('⚡ Reminder time has already passed, executing immediately');
          return {
            success: true,
            message: 'Reminder time has passed, action executed immediately'
          };
        }
        
        console.log(`⏳ Waiting ${Math.round(waitTimeMs / 1000 / 60)} minutes until reminder time`);
        
        // Perform the scheduled wait
        await new Promise(resolve => setTimeout(resolve, waitTimeMs));
        
        console.log('✅ Reminder wait completed, continuing automation');
        return {
          success: true,
          message: `Reminder executed ${reminderHoursBefore} hours before ${reminderReference}`,
          reminder_scheduled_for: reminderDateTime.toISOString(),
          appointment_time: appointmentDateTime.toISOString()
        };
        
      } catch (error) {
        console.error('❌ Error calculating reminder time:', error);
        return {
          success: false,
          message: 'Error calculating reminder schedule'
        };
      }
      
    } else {
      // Duration mode - standard wait for fixed duration
      const durationMinutes = config.duration_minutes || config.duration || 30;
      const waitTimeMs = durationMinutes * 60 * 1000;
      
      console.log(`⏱️ Duration mode: waiting ${durationMinutes} minutes (${waitTimeMs}ms)`);
      
      // Perform the wait
      await new Promise(resolve => setTimeout(resolve, waitTimeMs));
      
      console.log('✅ Duration wait completed, continuing automation');
      return {
        success: true,
        message: `Waited ${durationMinutes} minutes`,
        duration_minutes: durationMinutes
      };
    }
    
  } catch (error) {
    console.error('❌ Wait action failed:', error);
    return {
      success: false,
      message: `Wait action failed: ${error instanceof Error ? error.message : 'Unknown error'}`
    };
  }
}

// Main workflow execution function
async function executeAutomationWorkflow(
  automationId: string,
  executionId: string,
  contextData: Record<string, any>
): Promise<void> {
  if (!supabase) throw new Error('Supabase client not initialized');

  try {
    console.log(`🚀 Starting workflow execution for automation: ${automationId}`);

    // Get automation nodes and connections (and the automation's name, for the sender stamp)
    const [nodesData, connectionsData, automationRow] = await Promise.all([
      supabase
        .from('automation_nodes')
        .select('*')
        .eq('automation_id', automationId)
        .order('created_at', { ascending: true }),
      supabase
        .from('automation_connections')
        .select('*')
        .eq('automation_id', automationId),
      supabase
        .from('automations_v2')
        .select('name')
        .eq('id', automationId)
        .maybeSingle()
    ]);

    if (nodesData.error) throw nodesData.error;
    if (connectionsData.error) throw connectionsData.error;

    const nodes = nodesData.data || [];
    const connections = connectionsData.data || [];
    const automationName: string = (automationRow.data as { name?: string } | null)?.name || 'Automation';

    // Build execution order using topological sort
    const executionOrder = buildExecutionOrder(nodes, connections);
    
    console.log(`📋 Execution order: ${executionOrder.length} steps`);

    // Execute each node in order with data pipeline
    let stepOrder = 1;
    let enrichedContextData = { ...contextData }; // Start with original context
    
    for (const node of executionOrder) {
      if (node.node_type === 'action') {
        console.log(`⚡ Executing step ${stepOrder}: ${node.name} (${node.node_subtype})`);
        console.log(`📋 Context data keys available:`, Object.keys(enrichedContextData));
        
        // Execute action and get result. Anything it sends is from the automation.
        const actionResult = await withSender(
          { kind: 'automation', id: automationId, name: automationName },
          () => executeAutomationActionWithResult(executionId, node, stepOrder, enrichedContextData),
        );
        
        // Add action result to context for next actions
        if (actionResult) {
          enrichedContextData = {
            ...enrichedContextData,
            [`action_${stepOrder}_result`]: actionResult,
            [`${node.node_subtype}_result`]: actionResult // Also add by action type
          };
          console.log(`📈 Added action result to context:`, Object.keys(actionResult));
        }
        
        stepOrder++;
      }
    }

    // Update execution as completed
    await updateAutomationExecution(executionId, {
      status: 'completed',
      completed_at: new Date().toISOString()
    });

    console.log(`✅ Workflow execution completed for automation: ${automationId}`);
  } catch (error) {
    console.error(`❌ Workflow execution failed for automation: ${automationId}`, error);
    
    // Update execution as failed
    await updateAutomationExecution(executionId, {
      status: 'failed',
      completed_at: new Date().toISOString(),
      error_message: error instanceof Error ? error.message : 'Unknown error'
    });
    
    throw error;
  }
}

// Build execution order using topological sort
function buildExecutionOrder(
  nodes: AutomationNode[],
  connections: AutomationConnection[]
): AutomationNode[] {
  // Start with trigger nodes (no incoming connections)
  const triggerNodes = nodes.filter(node => 
    node.node_type === 'trigger' && 
    !connections.some(conn => conn.target_node_id === node.id)
  );

  // Build execution order starting from triggers
  const executionOrder: AutomationNode[] = [];
  const visited = new Set<string>();
  const visiting = new Set<string>();

  function visit(nodeId: string) {
    if (visiting.has(nodeId)) {
      throw new Error('Circular dependency detected in automation workflow');
    }
    if (visited.has(nodeId)) return;

    visiting.add(nodeId);
    
    const node = nodes.find(n => n.id === nodeId);
    if (!node) return;

    // Visit all nodes that this node depends on (incoming connections)
    const incomingConnections = connections.filter(conn => conn.target_node_id === nodeId);
    for (const connection of incomingConnections) {
      visit(connection.source_node_id);
    }

    visiting.delete(nodeId);
    visited.add(nodeId);
    executionOrder.push(node);
  }

  // Start with trigger nodes
  for (const trigger of triggerNodes) {
    visit(trigger.id);
  }

  // Visit any remaining nodes
  for (const node of nodes) {
    if (!visited.has(node.id)) {
      visit(node.id);
    }
  }

  return executionOrder;
}

// Trigger automation execution for form submission events using QStash
export async function triggerFormSubmissionAutomations(
  businessId: string,
  formId: string,
  formData: Record<string, any>
): Promise<void> {
  if (!supabase) return;

  try {
    console.log(`🎯 Triggering QStash-based form submission automations for form: ${formId}`);

    // Find live automations with form submission triggers that match this form
    const { data: automations, error: automationsError } = await supabase
      .from('automations_v2')
      .select(`
        id,
        name,
        automation_nodes!inner(
          id,
          node_type,
          node_subtype,
          configuration
        )
      `)
      .eq('business_id', businessId)
      .eq('is_live', true)
      .eq('automation_nodes.node_type', 'trigger')
      .eq('automation_nodes.node_subtype', 'form_submission');

    if (automationsError) throw automationsError;

    console.log(`📋 Found ${automations?.length || 0} potential form submission automations`);

    // Filter automations by form type and specific form ID
    const matchingAutomations = [];
    for (const automation of automations || []) {
      const formTriggers = automation.automation_nodes?.filter(
        (node: any) => 
          node.node_type === 'trigger' && 
          node.node_subtype === 'form_submission' &&
          (
            // Match lead forms by specific form ID
            (node.configuration?.formType === 'lead_form' && node.configuration?.formId === formId) ||
            // Match online booking banner (no specific form ID needed)
            (node.configuration?.formType === 'online_booking_banner')
          )
      ) || [];

      if (formTriggers.length > 0) {
        matchingAutomations.push(automation);
      }
    }

    console.log(`✅ Found ${matchingAutomations.length} matching automations for form: ${formId}`);

    // Start QStash-based execution for each matching automation
    for (const automation of matchingAutomations) {
      try {
        console.log(`🚀 Starting QStash form submission automation: ${automation.name}`);

        // Create execution record
        const execution = await createAutomationExecution(automation.id, {
          form_data: formData,
          form_id: formId,
          trigger_type: 'form_submission',
          form_type: 'lead_form',
          triggered_at: new Date().toISOString()
        });

        console.log(`📝 Created execution record: ${execution.id}`);

        // Start the QStash-based workflow
        await startQStashAutomation(automation.id, execution.id, {
          form_data: formData,
          form_id: formId,
          trigger_type: 'form_submission',
          form_type: 'lead_form',
          business_id: businessId
        });

        console.log(`✅ Initiated QStash workflow for: ${automation.name}`);
      } catch (error) {
        console.error(`❌ Failed to start QStash form submission automation: ${automation.name}`, error);
      }
    }
  } catch (error) {
    console.error('❌ Error triggering QStash form submission automations:', error);
    // Don't throw here to avoid breaking the form submission flow
  }
}

// Trigger automation execution for online booking banner form submissions using QStash
export async function triggerOnlineBookingBannerAutomations(
  businessId: string,
  formData: Record<string, any>
): Promise<void> {
  if (!supabase) return;

  try {
    console.log(`🎪 Triggering QStash-based online booking banner automations`);

    // Find live automations with form submission triggers for online booking banner
    const { data: automations, error: automationsError } = await supabase
      .from('automations_v2')
      .select(`
        id,
        name,
        automation_nodes!inner(
          id,
          node_type,
          node_subtype,
          configuration
        )
      `)
      .eq('business_id', businessId)
      .eq('is_live', true)
      .eq('automation_nodes.node_type', 'trigger')
      .eq('automation_nodes.node_subtype', 'form_submission');

    if (automationsError) throw automationsError;

    console.log(`📋 Found ${automations?.length || 0} potential form submission automations`);

    // Filter automations for online booking banner form type
    const matchingAutomations = [];
    for (const automation of automations || []) {
      const bannerTriggers = automation.automation_nodes?.filter(
        (node: any) => 
          node.node_type === 'trigger' && 
          node.node_subtype === 'form_submission' &&
          node.configuration?.formType === 'online_booking_banner'
      ) || [];

      if (bannerTriggers.length > 0) {
        matchingAutomations.push(automation);
      }
    }

    console.log(`✅ Found ${matchingAutomations.length} matching automations for online booking banner`);

    // Start QStash-based execution for each matching automation
    for (const automation of matchingAutomations) {
      try {
        console.log(`🚀 Starting QStash online booking banner automation: ${automation.name}`);

        // Create execution record
        const execution = await createAutomationExecution(automation.id, {
          form_data: formData,
          trigger_type: 'form_submission',
          form_type: 'online_booking_banner',
          triggered_at: new Date().toISOString()
        });

        console.log(`📝 Created execution record: ${execution.id}`);

        // Start the QStash-based workflow
        await startQStashAutomation(automation.id, execution.id, {
          form_data: formData,
          trigger_type: 'form_submission',
          form_type: 'online_booking_banner',
          business_id: businessId
        });

        console.log(`✅ Initiated QStash workflow for: ${automation.name}`);
      } catch (error) {
        console.error(`❌ Failed to start QStash online booking banner automation: ${automation.name}`, error);
      }
    }
  } catch (error) {
    console.error('❌ Error triggering QStash online booking banner automations:', error);
    // Don't throw here to avoid breaking the form submission flow
  }
}

/**
 * Fire the "booking created" automations for a booking that JUST got scheduled
 * out of the unscheduled ("limbo") list. Unscheduled bookings suppress the
 * created trigger at creation time; this is the moment it actually counts.
 * Loads the booking fresh so the payload has the newly assigned dates.
 */
export async function triggerBookingCreatedAfterScheduling(
  businessId: string,
  bookingId: string,
): Promise<void> {
  if (!supabase) return;
  try {
    const { data: booking } = await supabase
      .from('bookings')
      .select(`
        id, booking_number, title, status, start_date, start_time, end_date, end_time,
        drop_off_date, drop_off_time, address, subtotal, total_amount,
        customer:customers!bookings_customer_id_fkey (first_name, last_name, email, phone)
      `)
      .eq('id', bookingId)
      .maybeSingle();
    if (!booking) return;

    const { data: services } = await supabase
      .from('booking_services')
      .select('name, price, duration_minutes')
      .eq('booking_id', bookingId);

    const customer = Array.isArray(booking.customer) ? booking.customer[0] : booking.customer;
    const bookingData: Record<string, any> = {
      booking_id: booking.id,
      booking_number: booking.booking_number,
      title: booking.title,
      customer: {
        full_name: `${customer?.first_name || ''} ${customer?.last_name || ''}`.trim(),
        first_name: customer?.first_name || '',
        last_name: customer?.last_name || '',
        phone: customer?.phone || undefined,
        email: customer?.email || undefined,
      },
      schedule: {
        start_date: booking.start_date,
        start_time: booking.start_time,
        end_date: booking.end_date,
        end_time: booking.end_time,
        drop_off_date: booking.drop_off_date,
        drop_off_time: booking.drop_off_time,
      },
      services: (services || []).map((s: any) => ({
        name: s.name,
        price: s.price,
        duration_minutes: s.duration_minutes || 60,
      })),
      booking_address: booking.address || '',
      status: booking.status,
      grand_total: booking.total_amount || booking.subtotal || 0,
      source: 'unscheduled_booking_scheduled',
    };

    await triggerBookingAutomations(businessId, 'created', bookingData);
  } catch (error) {
    console.error('Failed to trigger created automations after scheduling:', error);
  }
}

// Trigger automation execution for booking events using QStash
export async function triggerBookingAutomations(
  businessId: string,
  eventType: 'created' | 'in_progress' | 'completed' | 'canceled' | 'deleted' | 'partially_paid' | 'paid',
  bookingData: Record<string, any>
): Promise<void> {
  if (!supabase) return;

  try {
    console.log(`🎯 Triggering QStash-based booking automations for event: ${eventType}`);

    // Unscheduled ("limbo") bookings never fire booking automations — they have
    // no date, so confirmations/reminders would be wrong or impossible. The
    // 'created' event fires later, when the booking actually gets scheduled.
    if (bookingData.booking_id) {
      try {
        const { data: limboCheck } = await supabase
          .from('bookings')
          .select('is_unscheduled')
          .eq('id', bookingData.booking_id)
          .maybeSingle();
        if (limboCheck?.is_unscheduled === true) {
          console.log(`⏸️ Booking ${bookingData.booking_id} is unscheduled — skipping ${eventType} automations`);
          return;
        }
      } catch (limboError) {
        console.error('Unscheduled check failed (continuing):', limboError);
      }
    }

    let enhancedBookingData = { ...bookingData };
    const feedbackEligibleEvents = ['in_progress', 'completed', 'paid', 'partially_paid'];

    // Resolve the business timezone up-front so reminder ("X hours before booking")
    // scheduling runs against local wall-clock time instead of the edge runtime's
    // UTC. The edge function also falls back to a businesses.timezone lookup.
    let businessTimezone: string | null = null;
    try {
      const { data: bizRow } = await supabase
        .from('businesses')
        .select('timezone')
        .eq('id', businessId)
        .single();
      businessTimezone = bizRow?.timezone || null;
    } catch (tzError) {
      console.error('Failed to fetch business timezone for automations:', tzError);
    }

    if (bookingData.booking_id) {
      try {
        const { data: bookingRecord, error: fetchError } = await supabase
          .from('bookings')
          .select('feedback_token')
          .eq('id', bookingData.booking_id)
          .single();

        if (fetchError) {
          console.error('Failed to fetch feedback token:', fetchError.message);
        } else if (bookingRecord?.feedback_token) {
          enhancedBookingData.feedback_token = bookingRecord.feedback_token;
        } else if (feedbackEligibleEvents.includes(eventType)) {
          try {
            const { generateFeedbackForBooking } = await import('../lib/feedback');
            const newToken = await generateFeedbackForBooking(bookingData.booking_id);
            if (newToken) {
              enhancedBookingData.feedback_token = newToken;
            }
          } catch (importError) {
            console.error('Failed to generate feedback token:', importError);
          }
        }
      } catch (error) {
        console.error('Unexpected error fetching feedback token:', error);
      }
    }

    // Find live automations with booking triggers that match this event type
    const { data: automations, error: automationsError } = await supabase
      .from('automations_v2')
      .select(`
        id,
        name,
        automation_nodes!inner(
          id,
          node_type,
          node_subtype,
          configuration
        )
      `)
      .eq('business_id', businessId)
      .eq('is_live', true)
      .eq('automation_nodes.node_type', 'trigger')
      .eq('automation_nodes.node_subtype', 'booking');

    if (automationsError) throw automationsError;

    console.log(`📋 Found ${automations?.length || 0} potential automations`);

    // Filter automations by event type and trigger them
    const matchingAutomations = [];
    for (const automation of automations || []) {
      const bookingTriggers = automation.automation_nodes?.filter(
        (node: any) => 
          node.node_type === 'trigger' && 
          node.node_subtype === 'booking' &&
          node.configuration?.eventType === eventType
      ) || [];

      if (bookingTriggers.length > 0) {
        matchingAutomations.push(automation);
      }
    }

    console.log(`✅ Found ${matchingAutomations.length} matching automations for event: ${eventType}`);

    // Start QStash-based execution for each matching automation
    for (const automation of matchingAutomations) {
      try {
        console.log(`🚀 Starting QStash automation: ${automation.name}`);

        // Create execution record
        const execution = await createAutomationExecution(automation.id, {
          booking_data: enhancedBookingData, // Use enhanced data with feedback token
          trigger_type: 'booking',
          event_type: eventType,
          business_timezone: businessTimezone,
          triggered_at: new Date().toISOString()
        });

        console.log(`📝 Created execution record: ${execution.id}`);

        // Start the QStash-based workflow
        console.log(`📤 Sending booking data to QStash automation:`, {
          automationId: automation.id,
          executionId: execution.id,
          bookingId: enhancedBookingData.booking_id,
          bookingNumber: enhancedBookingData.booking_number,
          customerPhone: enhancedBookingData.customer?.phone,
          customerFirstName: enhancedBookingData.customer?.first_name,
          hasBusinessId: !!enhancedBookingData.business_id,
          businessId: businessId
        });
        
        await startQStashAutomation(automation.id, execution.id, {
          booking_data: enhancedBookingData, // Use enhanced data with feedback token
          trigger_type: 'booking',
          event_type: eventType,
          business_id: businessId,
          business_timezone: businessTimezone
        });

        console.log(`✅ Initiated QStash workflow for: ${automation.name}`);
      } catch (error) {
        console.error(`❌ Failed to start QStash automation: ${automation.name}`, error);
      }
    }
  } catch (error) {
    console.error('❌ Error triggering QStash automations:', error);
    // Don't throw here to avoid breaking the booking creation flow
  }
}

/**
 * Calculate average rating from feedback data
 */
function calculateAverageRating(feedback: BookingFeedback): number {
  let totalRating = 0;
  let ratingCount = 0;

  // Check form_responses first (new dynamic form system)
  if (feedback.form_responses && Object.keys(feedback.form_responses).length > 0) {
    Object.values(feedback.form_responses).forEach(value => {
      if (typeof value === 'number' && value > 0) {
        totalRating += value;
        ratingCount++;
      }
    });
  }

  // Fallback to legacy fields if no form_responses
  if (ratingCount === 0) {
    if (feedback.overall_quality_rating) {
      totalRating += feedback.overall_quality_rating;
      ratingCount++;
    }
    if (feedback.overall_experience_rating) {
      totalRating += feedback.overall_experience_rating;
      ratingCount++;
    }
  }

  return ratingCount > 0 ? totalRating / ratingCount : 0;
}

/**
 * Trigger automation execution for feedback results using QStash
 * This function is called when feedback is submitted and matches the rating condition
 */
export async function triggerFeedbackAutomations(
  feedback: BookingFeedback
): Promise<void> {
  if (!supabase) return;

  try {
    console.log(`🎯 Triggering QStash-based feedback automations for booking: ${feedback.booking_id}`);

    // Calculate average rating from feedback
    const averageRating = calculateAverageRating(feedback);
    console.log(`⭐ Calculated average rating: ${averageRating.toFixed(2)}`);

    if (averageRating === 0) {
      console.log('⚠️ No valid ratings found in feedback, skipping automation trigger');
      return;
    }

    // Load full booking data (similar to booking trigger)
    const booking = await getBooking(feedback.booking_id);
    if (!booking) {
      console.error(`❌ Booking not found: ${feedback.booking_id}`);
      return;
    }

    // Prepare booking data for automation (same structure as booking trigger)
    const bookingData = {
      booking_id: booking.id,
      booking_number: booking.booking_number,
      title: booking.title,
      status: booking.status,
      customer: booking.customer ? {
        id: booking.customer.id,
        first_name: booking.customer.first_name,
        last_name: booking.customer.last_name,
        email: booking.customer.email,
        phone: booking.customer.phone
      } : null,
      services: booking.services || [],
      resources: booking.resources || [],
      payments: booking.payments || [],
      start_date: booking.start_date,
      start_time: booking.start_time,
      end_date: booking.end_date,
      end_time: booking.end_time,
      drop_off_date: booking.drop_off_date,
      drop_off_time: booking.drop_off_time,
      total_amount: booking.total_amount,
      subtotal: booking.subtotal,
      tax_amount: booking.tax_amount,
      business_id: booking.business_id
    };

    // Add feedback data to context
    const feedbackData = {
      feedback_id: feedback.id,
      average_rating: averageRating,
      overall_quality_rating: feedback.overall_quality_rating,
      overall_experience_rating: feedback.overall_experience_rating,
      improvement_suggestions: feedback.improvement_suggestions,
      form_responses: feedback.form_responses,
      submitted_at: feedback.submitted_at
    };

    // Find live automations with feedback triggers that match the rating condition
    const { data: automations, error: automationsError } = await supabase
      .from('automations_v2')
      .select(`
        id,
        name,
        automation_nodes!inner(
          id,
          node_type,
          node_subtype,
          configuration
        )
      `)
      .eq('business_id', booking.business_id)
      .eq('is_live', true)
      .eq('automation_nodes.node_type', 'trigger')
      .eq('automation_nodes.node_subtype', 'feedback');

    if (automationsError) throw automationsError;

    console.log(`📋 Found ${automations?.length || 0} potential feedback automations`);

    // Filter automations by rating condition
    const matchingAutomations = [];
    for (const automation of automations || []) {
      const feedbackTriggers = automation.automation_nodes?.filter(
        (node: any) => 
          node.node_type === 'trigger' && 
          node.node_subtype === 'feedback' &&
          node.configuration?.minRating &&
          averageRating >= node.configuration.minRating
      ) || [];

      if (feedbackTriggers.length > 0) {
        matchingAutomations.push(automation);
        console.log(`✅ Automation "${automation.name}" matches (minRating: ${feedbackTriggers[0].configuration.minRating}, actual: ${averageRating.toFixed(2)})`);
      }
    }

    console.log(`✅ Found ${matchingAutomations.length} matching automations for feedback rating: ${averageRating.toFixed(2)}`);

    // Start QStash-based execution for each matching automation
    for (const automation of matchingAutomations) {
      try {
        console.log(`🚀 Starting QStash automation: ${automation.name}`);

        // Create execution record
        const execution = await createAutomationExecution(automation.id, {
          booking_data: bookingData,
          feedback_data: feedbackData,
          trigger_type: 'feedback',
          average_rating: averageRating,
          triggered_at: new Date().toISOString()
        });

        console.log(`📝 Created execution record: ${execution.id}`);

        // Start the QStash-based workflow
        await startQStashAutomation(automation.id, execution.id, {
          booking_data: bookingData,
          feedback_data: feedbackData,
          trigger_type: 'feedback',
          average_rating: averageRating,
          business_id: booking.business_id
        });

        console.log(`✅ Initiated QStash workflow for: ${automation.name}`);
      } catch (error) {
        console.error(`❌ Failed to start QStash automation: ${automation.name}`, error);
      }
    }
  } catch (error) {
    console.error('❌ Error triggering QStash feedback automations:', error);
    // Don't throw here to avoid breaking the feedback submission flow
  }
}

// Start QStash-based automation execution
async function startQStashAutomation(
  automationId: string,
  executionId: string,
  contextData: Record<string, any>
): Promise<void> {
  if (!supabase) throw new Error('Supabase client not initialized');

  try {
    // Create a unique job ID
    const jobId = crypto.randomUUID();

    console.log(`🎯 Starting QStash automation - Job ID: ${jobId}`);

    // Call the execute-automation-step edge function to start the workflow
    const { data, error } = await supabase.functions.invoke('execute-automation-step', {
      body: {
        jobId,
        automationId,
        executionId,
        currentStep: 1, // Start with step 1
        contextData
      }
    });

    if (error) {
      throw new Error(`Failed to start QStash automation: ${error.message}`);
    }

    console.log(`✅ QStash automation started successfully:`, data);

    // Extract booking_id from context data for easier lookups
    // The booking_data.booking_id is already a UUID, no lookup needed
    const bookingId = contextData?.booking_data?.booking_id || contextData?.booking_data?.id || null;
    if (bookingId) {
      console.log(`✅ Using booking UUID from context: ${bookingId}`);
    }
    
    // Create initial job tracking record
    await supabase
      .from('automation_jobs')
      .insert({
        id: jobId,
        qstash_message_id: data?.messageId || null,
        automation_id: automationId,
        execution_id: executionId,
        scheduled_for: new Date().toISOString(),
        current_step: 1,
        status: 'scheduled',
        context_data: contextData,
        booking_id: bookingId // Add booking_id for fast lookups
      });

    console.log(`📝 Created job tracking record: ${jobId}`);

  } catch (error) {
    console.error('❌ Failed to start QStash automation:', error);
    
    // Update execution record as failed
    await updateAutomationExecution(executionId, {
      status: 'failed',
      error_message: error instanceof Error ? error.message : 'Failed to start QStash automation'
    });
    
    throw error;
  }
}

// Trigger automation execution for missed call events using QStash
export async function triggerMissedCallAutomations(
  businessId: string,
  callData: {
    caller_phone: string;
    caller_name?: string;
    call_sid?: string;
    call_time: string;
    from_number: string;
    to_number: string;
  }
): Promise<void> {
  if (!supabase) return;

  try {
    console.log(`📞 Triggering QStash-based missed call automations for business: ${businessId}`);
    console.log(`📞 Call data:`, callData);

    // Find live automations with missed_call triggers
    const { data: automations, error: automationsError } = await supabase
      .from('automations_v2')
      .select(`
        id,
        name,
        automation_nodes!inner(
          id,
          node_type,
          node_subtype,
          configuration
        )
      `)
      .eq('business_id', businessId)
      .eq('is_live', true)
      .eq('automation_nodes.node_type', 'trigger')
      .eq('automation_nodes.node_subtype', 'missed_call');

    if (automationsError) throw automationsError;

    console.log(`📋 Found ${automations?.length || 0} missed call automations`);

    // Start QStash-based execution for each matching automation
    for (const automation of automations || []) {
      try {
        console.log(`🚀 Starting QStash missed call automation: ${automation.name}`);

        // Create execution record
        const execution = await createAutomationExecution(automation.id, {
          call_data: callData,
          trigger_type: 'missed_call',
          triggered_at: new Date().toISOString()
        });

        console.log(`📝 Created execution record: ${execution.id}`);

        // Start the QStash-based workflow
        await startQStashAutomation(automation.id, execution.id, {
          call_data: callData,
          trigger_type: 'missed_call',
          business_id: businessId,
          // Map call data to standard variables for SMS/email actions
          customer: {
            phone: callData.caller_phone,
            name: callData.caller_name || 'Unknown Caller'
          }
        });

        console.log(`✅ Initiated QStash workflow for: ${automation.name}`);
      } catch (error) {
        console.error(`❌ Failed to start QStash missed call automation: ${automation.name}`, error);
      }
    }
  } catch (error) {
    console.error('❌ Error triggering QStash missed call automations:', error);
    // Don't throw here to avoid breaking the call handling flow
  }
}

async function fetchOpportunityAutomationContext(
  opportunityId: number,
  opts?: { previousStageId?: number | null; previousStageName?: string | null }
): Promise<OpportunityAutomationData | null> {
  if (!supabase) return null;

  const { data: opportunity, error: oppError } = await supabase
    .from('pipeline_opportunities')
    .select('*')
    .eq('id', opportunityId)
    .maybeSingle();

  if (oppError || !opportunity) {
    console.error('Failed to load opportunity for enter_crm_stage trigger:', oppError);
    return null;
  }

  let customer: Record<string, unknown> | null = null;
  if (opportunity.customer_id) {
    const { data: customerRow } = await supabase
      .from('customers')
      .select('id, first_name, last_name, email, phone')
      .eq('id', opportunity.customer_id)
      .maybeSingle();
    customer = customerRow;
  }

  const { data: pipeline } = await supabase
    .from('pipelines')
    .select('pipeline_name, stages')
    .eq('id', opportunity.pipeline_id)
    .maybeSingle();

  const pipelineName = pipeline?.pipeline_name || 'Unknown Pipeline';
  const stageName = resolveStageName(pipeline?.stages, opportunity.stage_id);
  const previousStageName =
    opts?.previousStageName ??
    (opts?.previousStageId != null
      ? resolveStageName(pipeline?.stages, opts.previousStageId)
      : null);

  return buildOpportunityAutomationData({
    opportunity,
    customer,
    pipelineName,
    stageName,
    previousStageId: opts?.previousStageId ?? null,
    previousStageName,
  });
}

function automationMatchesEnterCrmStageTrigger(
  nodes: Array<{ node_type?: string; node_subtype?: string; configuration?: Record<string, unknown> }>,
  pipelineId: number,
  stageId: number
): boolean {
  return (nodes || []).some((node) => {
    if (node.node_type !== 'trigger' || node.node_subtype !== 'enter_crm_stage') return false;
    const cfg = node.configuration || {};
    return (
      Number(cfg.pipeline_id) === Number(pipelineId) && Number(cfg.stage_id) === Number(stageId)
    );
  });
}

/** Fire live automations when an opportunity enters a configured CRM stage. */
export async function triggerEnterStageAutomations(
  businessId: string,
  opportunityId: number,
  opts?: { previousStageId?: number | null; previousStageName?: string | null }
): Promise<void> {
  if (!supabase) return;

  try {
    const opportunityData = await fetchOpportunityAutomationContext(opportunityId, opts);
    if (!opportunityData) return;

    if (
      opts?.previousStageId != null &&
      Number(opts.previousStageId) === Number(opportunityData.stage_id)
    ) {
      return;
    }

    const { data: automations, error: automationsError } = await supabase
      .from('automations_v2')
      .select(`
        id,
        name,
        automation_nodes!inner(
          id,
          node_type,
          node_subtype,
          configuration
        )
      `)
      .eq('business_id', businessId)
      .eq('is_live', true)
      .eq('automation_nodes.node_type', 'trigger')
      .eq('automation_nodes.node_subtype', 'enter_crm_stage');

    if (automationsError) throw automationsError;

    const matchingAutomations = (automations || []).filter((automation) =>
      automationMatchesEnterCrmStageTrigger(
        automation.automation_nodes,
        opportunityData.pipeline_id,
        opportunityData.stage_id
      )
    );

    console.log(
      `✅ Found ${matchingAutomations.length} enter_crm_stage automations for pipeline ${opportunityData.pipeline_id}, stage ${opportunityData.stage_id}`
    );

    for (const automation of matchingAutomations) {
      try {
        const execution = await createAutomationExecution(automation.id, {
          opportunity_data: opportunityData,
          trigger_type: 'enter_crm_stage',
          pipeline_id: opportunityData.pipeline_id,
          stage_id: opportunityData.stage_id,
          previous_stage_id: opportunityData.previous_stage_id,
          triggered_at: new Date().toISOString(),
        });

        await startQStashAutomation(automation.id, execution.id, {
          opportunity_data: opportunityData,
          trigger_type: 'enter_crm_stage',
          business_id: businessId,
          customer: opportunityData.customer,
        });

        console.log(`✅ Started enter_crm_stage automation: ${automation.name}`);
      } catch (error) {
        console.error(`❌ Failed to start enter_crm_stage automation: ${automation.name}`, error);
      }
    }
  } catch (error) {
    console.error('❌ Error triggering enter_crm_stage automations:', error);
  }
}
 